import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import {
  getPaginatedEvents,
  createEvents,
  updateEvents,
  deleteEvents,
  exportEventsPdfReport,
} from '../api/eventsAPI';
import { getEventStatus } from '../api/event-statusAPI';
import { getEventTypes } from '../api/event-typeAPI';
import { getPaginatedAssets } from '../api/assetAPI';
import { getEventResponsibilities } from '../api/event-responsibilityAPI';
import {
  getEventDelayByEventId,
  createEventDelay,
  updateEventDelay,
  deleteEventDelay,
} from '../api/event-delayAPI';
import { getEventDelayCodes } from '../api/event-delay-codeAPI';
import { createEventComment, getEventCommentsByEventId } from '../api/event-commentsAPI';
import {
  createWorkOrder,
  getPaginatedWorkOrders,
  getWorkOrderById,
  updateWorkOrder,
} from '../api/work-orderAPI';
import { getWorkOrderStatus } from '../api/work-order-statusAPI';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useTableFilters } from '../utils/useTableFilters';
import { useValidator } from '../utils/useValidator';
import { useFormBuilder } from '../utils/useFormBuilder';
import EventDataTable from './EventDataTable';
import EventEditModal from './EventEditModal';
import WorkOrderEditModal from './WorkOrderEditModal';
import { useSetting } from '../utils/AppSettingsContext';
import ShiftLogManager from './ShiftLogManager';
import { useModule } from '../utils/useModule';
import {
  formatIntDateTime,
  toLocalInputFormat,
  formatDateTime,
  toUtcIsoWithTime,
  parseUtc,
} from '../utils/dateUtils';
import { computeDefaultDelayStartDate, validateDelayDateRange } from '../utils/eventDelayRules';
import {
  getEventRange,
  hasWorkOrdersOutsideEventRange,
  buildCompleteWorkOrderPayload,
  buildAlignmentPayload,
  areWorkOrdersCompletableForEvent,
} from '../utils/eventWorkOrderRules';

const parseQueryValues = (searchParams, key) => {
  if (!searchParams) return [];
  return Array.from(
    new Set(
      searchParams
        .getAll(key)
        .flatMap((value) => String(value).split(','))
        .map((value) => value.trim().replace(/^\[/, '').replace(/\]$/, ''))
        .filter(Boolean),
    ),
  );
};

const normalizeEventTab = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return 'all';
  if (normalized === 'in progress') return 'inProgress';
  if (normalized === 'planned' || normalized === 'pending') return 'planned';
  if (normalized === 'complete' || normalized === 'completed') return 'done';
  if (normalized === 'all') return 'all';
  return 'all';
};

const formatStatusLabel = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
};

const calcDelayDuration = (delay) =>
  delay?.delayStartDate
    ? ((new Date(delay.delayEndDate || new Date()) - new Date(delay.delayStartDate)) / (1000 * 60 * 60))
    : 0;

const formatDelayDuration = (delay) => {
  if (!delay?.delayStartDate) {
    return '';
  }

  const start = new Date(delay.delayStartDate);
  const end = new Date(delay.delayEndDate || new Date());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '';
  }

  const totalMinutes = Math.max(0, Math.round((end - start) / (1000 * 60)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  if (parts.length === 0) {
    parts.push('0m');
  }

  return parts.join(' ');
};

const formatDelayList = (delays = []) => {
  if (!Array.isArray(delays) || delays.length === 0) {
    return '';
  }

  return delays
    .map((delay, index) => {
      const label =
        delay?.eventDelayCode?.name || delay?.eventDelayCode?.code || `Delay ${index + 1}`;
      const duration = formatDelayDuration(delay);
      return duration ? `${label} (${duration})` : label;
    })
    .join(', ');
};

const formatDelayTotalHHMM = (delays = []) => {
  if (!Array.isArray(delays) || delays.length === 0) {
    return '00:00';
  }

  const totalMinutes = delays.reduce((sum, delay) => {
    if (!delay?.delayStartDate) {
      return sum;
    }
    const start = new Date(delay.delayStartDate);
    const end = new Date(delay.delayEndDate || new Date());
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return sum;
    }
    const minutes = Math.max(0, Math.round((end - start) / (1000 * 60)));
    return sum + minutes;
  }, 0);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatDurationHHMM = (startValue, endValue) => {
  if (!startValue || !endValue) return '';
  const start = new Date(startValue);
  const end = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '';
  }
  const totalMinutes = Math.max(0, Math.round((end - start) / (1000 * 60)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const normalizePhotos = (value) => {
  if (!value) return [];

  const toArray = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      try {
        const parsed = JSON.parse(input);
        return toArray(parsed);
      } catch (err) {
        return [input];
      }
    }
    if (typeof input === 'object') {
      if (input === null) return [];
      const values = Object.values(input).flat();
      return values.flatMap((entry) => toArray(entry));
    }
    return [];
  };

  return toArray(value)
    .map((entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') return entry;
      if (typeof entry === 'object') {
        if (typeof entry.key === 'string') return entry.key;
        if (typeof entry.filename === 'string') return entry.filename;
        if (typeof entry.url === 'string') return entry.url;
      }
      return null;
    })
    .filter((item, index, arr) => item && arr.indexOf(item) === index);
};

const normalizeComment = (comment) => {
  if (!comment) return null;
  const photos = normalizePhotos(comment.photos);
  const createdAt = comment.createdAt || comment.created_at || null;
  const user = comment.user || comment.User || null;

  return {
    ...comment,
    photos,
    createdAt,
    user,
  };
};

const normalizeCommentList = (list) => {
  const array = Array.isArray(list) ? list : [];
  const normalized = array
    .map((item) => normalizeComment(item))
    .filter(Boolean);

  return normalized.sort((a, b) => {
    const getTime = (value) => {
      const raw = value?.createdAt || value?.created_at;
      if (!raw) return 0;
      const date = parseUtc(raw);
      const timestamp = date.getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    return getTime(b) - getTime(a);
  });
};

const formatCommentTimestamp = (value) => {
  if (!value) return '';
  const date = parseUtc(value);
  if (!Number.isFinite(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const getUserImageKey = (user) => {
  if (!user) return null;
  return user.userImage || user.user_image || user.image || null;
};

const getUserInitials = (name) => {
  if (!name) return '';
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const EventsLayer = () => {
  const userProfile = useUserProfile();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const listContainerRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [eventPage, setEventPage] = useState(1);
  const [eventHasMore, setEventHasMore] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('eventType');
  const [sortOrder, setSortOrder] = useState('asc');
  const [activeTab, setActiveTab] = useState(() => normalizeEventTab(searchParams.get('tab'))); // Set AllEvents as active tab
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [eventResponsibilityOptions, setEventResponsibilityOptions] = useState([]);
  const [defaultEventResponsibilityId, setDefaultEventResponsibilityId] = useState(null);
  const [assignedWorkOrders, setAssignedWorkOrders] = useState([]);
  const [availableWorkOrders, setAvailableWorkOrders] = useState([]);
  const [workOrderStatuses, setWorkOrderStatuses] = useState([]);
  const [showAddWorkOrderModal, setShowAddWorkOrderModal] = useState(false);
  const [workOrderModalLoading, setWorkOrderModalLoading] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState({});
  const [workOrderAssetOption, setWorkOrderAssetOption] = useState(null);
  const [eventDelays, setEventDelays] = useState([]);
  const [eventComments, setEventComments] = useState([]);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsHistory, setCommentsHistory] = useState([]);
  const [commentsEvent, setCommentsEvent] = useState(null);
  const [showDelaysListModal, setShowDelaysListModal] = useState(false);
  const [delaysListLoading, setDelaysListLoading] = useState(false);
  const [delaysHistory, setDelaysHistory] = useState([]);
  const [delaysEvent, setDelaysEvent] = useState(null);
  const [delayCodes, setDelayCodes] = useState([]);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [editingDelay, setEditingDelay] = useState(null);
  const [delayForm, setDelayForm] = useState({
    eventDelayCodeId: '',
    delayStartDate: '',
    delayEndDate: '',
  });
  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);
  const delayValidationFields = useMemo(
    () => {
      const isComplete = Number(newEvent?.eventStatus) === 2;
      return [
      { field: 'eventDelayCodeId', label: 'Delay Code', required: true },
      { field: 'delayStartDate', label: 'Delay Start Date', required: true, type: 'datetime' },
      { field: 'delayEndDate', label: 'Delay End Date', required: isComplete, type: 'datetime' },
      ];
    },
    [newEvent?.eventStatus],
  );
  const {
    errors: delayErrors,
    setErrors: setDelayErrors,
    validate: validateDelayForm,
  } = useValidator(delayValidationFields, delayForm);
  const [delayDuration, setDelayDuration] = useState(0);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOptions, setAssignOptions] = useState([]);
  const [assignSelected, setAssignSelected] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [showEditWoModal, setShowEditWoModal] = useState(false);
  const [editingWo, setEditingWo] = useState(null);
  const [editWoForm, setEditWoForm] = useState({});
  const [editWoErrors, setEditWoErrors] = useState({});
  const [showShiftLogManager, setShowShiftLogManager] = useState(false);
  const [conflictModal, setConflictModal] = useState(null);
  const [eventDialog, setEventDialog] = useState(null);
  const eventDialogResolverRef = useRef(null);

  const showEventDialog = useCallback((dialog) => new Promise((resolve) => {
    eventDialogResolverRef.current = resolve;
    setEventDialog(dialog);
  }), []);

  const closeEventDialog = useCallback((result) => {
    const resolver = eventDialogResolverRef.current;
    eventDialogResolverRef.current = null;
    setEventDialog(null);
    if (resolver) {
      resolver(result);
    }
  }, []);

  const hiddenColumns = useMemo(() => {
    switch (activeTab) {
      case 'planned':
        return ['start', 'end'];
      case 'inProgress':
        return ['plannedStart', 'end'];
      case 'done':
        return ['plannedStart', 'plannedEnd'];
      default:
        return [];
    }
  }, [activeTab]);

  // Get Event Settings
  const showSystem = useSetting('event_system');
  const eventTechsSetting = useSetting('event_techs');
  const completeEventWorkOrdersSetting = useSetting('complete_event_work_orders', false);
  const eventTechsEnabled = Number(eventTechsSetting) === 1;
  const isShiftLogModuleEnabled = useModule('shiftlog');

  const canViewShiftLog = !!(userProfile?.privileges?.includes('shift-log:view'));

  const eventValidationFields = useMemo(() => {
    const status = Number(newEvent?.eventStatus);
    const eventType = Number(newEvent?.eventTypeId);
    const isBreakdownEvent = eventType === 2;
    const isInProgress = status === 1;
    const isComplete = status === 2;
    const isCancelled = status === 3;
    const isPending = status === 4;

    const plannedStartRequired = !(
      (isInProgress && !isBreakdownEvent) ||
      (isComplete && !isBreakdownEvent) ||
      isCancelled ||
      isComplete
    );

    const plannedEndRequired = !(
      isComplete || isCancelled
    );

    const startRequired = (isInProgress || isComplete) && !isCancelled;
    const endRequired = isComplete && !isCancelled;

    return [
      { field: 'name', label: 'Name', required: true },
      { field: 'eventTypeId', label: 'Event Type', required: true },
      { field: 'assetId', label: 'Asset', required: true },
      { field: 'eventLocationId', label: 'Event Location' },
      { field: 'eventStatus', label: 'Status', required: true },
      {
        field: 'plannedStartDate',
        label: 'Planned Start Date',
        required: plannedStartRequired,
        type: 'datetime',
      },
      {
        field: 'plannedEndDate',
        label: 'Planned End Date',
        required: plannedEndRequired,
        type: 'datetime',
      },
      { field: 'startDate', label: 'Start Date', required: startRequired, type: 'datetime' },
      { field: 'endDate', label: 'End Date', required: endRequired, type: 'datetime' },
    ];
  }, [newEvent?.eventStatus, newEvent?.eventTypeId]);

  const {
    errors: newEventErrors,
    setErrors: setNewEventErrors,
    validate: validateNewEvent,
  } = useValidator(eventValidationFields, newEvent);

  const workOrderValidationFields = useMemo(
    () => [
      { field: 'workOrderTitle', label: 'Title', required: true },
      { field: 'assetId', label: 'Asset', required: true },
      { field: 'statusId', label: 'Status', required: true },
      { field: 'taskTypeId', label: 'Task Type', required: true },
      { field: 'priorityId', label: 'Priority', required: true },
    ],
    [],
  );

  const {
    errors: workOrderErrors,
    setErrors: setWorkOrderErrors,
    validate: validateWorkOrder,
  } = useValidator(workOrderValidationFields, newWorkOrder);

  const editWoFields = useMemo(
    () => [
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: 6,
        api: 'work-order-status',
        displayField: 'name',
        required: true,
        formatOptionLabel: (opt) => (
          <div className="d-flex align-items-center gap-2">
            <span
              className={`d-inline-block rounded-1 bg-${opt.bootstrapName}`}
              style={{ width: '10px', height: '10px' }}
            ></span>
            {opt.label}
          </div>
        ),
      },
      { field: 'startedAt', label: 'Started At', type: 'datetime', size: 6 },
      { field: 'completedAt', label: 'Completed At', type: 'datetime', size: 6 },
      { field: 'failureId', label: 'Failure', type: 'select', size: 6, api: 'failure-codes', displayField: 'name' },
      { field: 'causeId', label: 'Cause', type: 'select', size: 6, api: 'cause-codes', displayField: 'name' },
      { field: 'symptom', label: 'Symptom', type: 'text', size: 6 },
      { field: 'notes', label: 'Completion Notes', type: 'textarea', size: 12 },
    ],
    []
  );

  const { renderField: renderEditWoField } = useFormBuilder(
    editWoFields,
    editWoForm,
    setEditWoForm,
    editWoErrors
  );

  const delayFields = useMemo(
    () => {
      const isComplete = Number(newEvent?.eventStatus) === 2;
      return [
      {
        field: 'eventDelayCodeId',
        label: 'Delay Code',
        type: 'select',
        size: 12,
        required: true,
        options: (delayCodes || []).map((code) => ({
          value: code.id,
          label: [code.code, code.name].filter(Boolean).join(' - '),
        })),
      },
      {
        field: 'delayStartDate',
        label: 'Start Date',
        type: 'datetime',
        size: 12,
        required: true,
      },
      {
        field: 'delayEndDate',
        label: 'End Date',
        type: 'datetime',
        size: 12,
        required: isComplete,
      },
      ];
    },
    [delayCodes, newEvent?.eventStatus],
  );

  const { renderField: renderDelayField } = useFormBuilder(
    delayFields,
    delayForm,
    setDelayForm,
    delayErrors,
  );

  const formatOptionColor = useCallback(
    (option) => (
      <div className="d-flex align-items-center gap-2">
        <span
          className={`d-inline-block rounded-circle bg-${option.colour || option.bootstrapName || 'secondary'}`}
          style={{ width: '10px', height: '10px' }}
        ></span>
        <span>{option.label}</span>
      </div>
    ),
    [],
  );

  const handleAssetSearch = async (inputValue) => {
    const res = await getPaginatedAssets({ page: 1, pageSize: 25, search: inputValue });
    const rows = res.data || [];
    const options = mapAssetsToOptions(rows);
    setAssetOptions(options);
    return options;
  };


  const workOrderGeneralFields = useMemo(
    () => [
      { field: 'workOrderTitle', label: 'Title', type: 'text', size: 8, required: true },
      {
        field: 'statusId',
        label: 'Status',
        type: 'select',
        size: 4,
        required: true,
        options: workOrderStatuses,
        formatOptionLabel: formatOptionColor,
      },
      { field: 'workOrderDescription', label: 'Description', type: 'textarea', size: 12, rows: 3 },
      {
        field: 'assetId',
        label: 'Asset',
        type: 'paginated-select',
        size: 4,
        required: true,
        loadOptions: handleAssetSearch,
        selectedOption: workOrderAssetOption,
        setSelectedOption: setWorkOrderAssetOption,
        options: assetOptions,
        isClearable: false,
        disabled: true,
      },
      { field: 'breakdown', label: 'Breakdown', type: 'switch', size: 1, required: false, switchColor: 'danger' },
      { field: 'statutory', label: 'Compliance', type: 'switch', size: 1, required: false, switchColor: 'danger' },
      { field: 'taskTypeId', label: 'Task Type', type: 'select', size: 4, required: true, api: 'task-types', displayField: 'taskTypeName' },
      { field: 'priorityId', label: 'Priority', type: 'select', size: 4, required: true, api: 'priority', displayField: 'name', formatOptionLabel: formatOptionColor },
      { field: 'scheduledAt', label: 'Scheduled Start', type: 'datetime', size: 4, required: false },
      { field: 'dueDate', label: 'Scheduled End', type: 'datetime', size: 4, required: false },
      { field: 'startedAt', label: 'Started', type: 'datetime', size: 4, required: false },
      { field: 'completedAt', label: 'Completed', type: 'datetime', size: 4, required: false },
    ],
    [
      assetOptions,
      formatOptionColor,
      handleAssetSearch,
      workOrderAssetOption,
      workOrderStatuses,
    ],
  );

  const { renderField: renderWorkOrderField } = useFormBuilder(
    workOrderGeneralFields,
    newWorkOrder,
    setNewWorkOrder,
    workOrderErrors,
  );

  const filteredAssignOptions = useMemo(() => {
    if (!assignSearch) return assignOptions;
    const searchLower = assignSearch.toLowerCase();
    return assignOptions.filter(
      (wo) =>
        (wo.workOrderNumber || '').toLowerCase().includes(searchLower) ||
        (wo.workOrderTitle || '').toLowerCase().includes(searchLower)
    );
  }, [assignOptions, assignSearch]);

  const mapEventStatusToWorkOrderStatus = useCallback((statusId) => {
    const numericStatus = Number(statusId);
    switch (numericStatus) {
      case 1:
        return 2;
      case 5:
        return 1;
      case 3:
        return 5;
      case 2:
        return 4;
      default:
        return 1;
    }
  }, []);

  useEffect(() => {
    if (delayForm.delayStartDate) {
      const endValue = delayForm.delayEndDate || new Date();
      const duration =
        (new Date(endValue) - new Date(delayForm.delayStartDate)) /
        (1000 * 60 * 60);
      setDelayDuration(Number.isFinite(duration) ? parseFloat(duration.toFixed(2)) : 0);
    } else {
      setDelayDuration(0);
    }
  }, [delayForm.delayStartDate, delayForm.delayEndDate]);

  useEffect(() => {
    getWorkOrderStatus().then((data) => {
      setWorkOrderStatuses(
        (data || []).map((s) => ({
          value: s.id,
          label: s.name,
          bootstrapName: s.colours?.bootstrapName || 'secondary',
        }))
      );
    });
  }, []);

  useEffect(() => {
    getEventStatus().then(data => {
      setStatusOptions((data || []).map(s => ({ value: s.id, label: s.name, colours: s.colours })));
    });
    getEventTypes().then(data => {
      setEventTypeOptions((data || []).map(t => ({ value: t.id, label: t.name })));
    });
    getEventResponsibilities()
      .then((data) => {
        const options = (data || []).map((responsibility) => ({
          value: responsibility.id,
          label: responsibility.name,
          isDefault: responsibility.default === 1 || responsibility.default === true,
        }));
        setEventResponsibilityOptions(options);
        const defaultOption = options.find((option) => option.isDefault);
        setDefaultEventResponsibilityId(defaultOption?.value ?? null);
      })
      .catch((error) => {
        console.error('Failed to load event responsibilities', error);
        setEventResponsibilityOptions([]);
        setDefaultEventResponsibilityId(null);
      });
    getEventDelayCodes().then(data => {
      setDelayCodes(data || []);
    });
  }, []);

  const tableFilters = useMemo(() => [
    {
      field: 'departmentId',
      type: 'dropDown',
      label: 'Site or Department',
      api: 'hierarchy-structures',
      displayField: 'name',
      menuBarFilter: 1,
    },
    { field: 'startDate', type: 'date', label: 'Start Date', api: '', displayField: '' },
    { field: 'endDate', type: 'date', label: 'End Date', api: '', displayField: '' },
    { field: 'eventTypeId', type: 'dropDown', label: 'Event Type', api: 'event-type', displayField: 'name', menuBarFilter: 1 },

    { field: 'assetId', type: 'dropDown', label: 'Asset', api: 'assets/for-dropdown', displayField: (a) => `${a.unitNumber || a.assetNumber || ''} ${a.assetDescription || ''}` },
    { field: 'assetClassId', type: 'dropDown', label: 'Asset Class', api: 'class', displayField: 'className', menuBarFilter: 1 },
    { field: 'assetGroupId', type: 'dropDown', label: 'Asset Group', api: 'groups', displayField: 'groupName', menuBarFilter: 0 },
    { field: 'systemId', type: 'dropDown', label: 'System', api: 'asset-system', displayField: 'name' },
    { field: 'eventResponsibilityId', type: 'dropDown', label: 'Event Responsibility', api: 'event-responsibility', displayField: 'name' },
    {
      field: 'dateRange',
      type: 'dropDown',
      label: 'Date Range',
      isMulti: false,
      menuBarFilter: 1,
      options: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'lastMonth', label: 'Last Month' },
        { value: 'thisYear', label: 'This year (Calendar)' },
      ],
    },
  ], []);

  
  const initialDepartmentIds = useMemo(() => {
    const departmentIds = parseQueryValues(searchParams, 'departmentIds');
    if (departmentIds.length) {
      return departmentIds;
    }
    return parseQueryValues(searchParams, 'departmentId');
  }, [searchParams]);

  
  const initialFilterValues = useMemo(() => {
    const initial = {};
    if (initialDepartmentIds.length) {
      initial.departmentId = initialDepartmentIds;
    }

    if (searchParams.get('startDate')) {
      initial.startDate = searchParams.get('startDate');
    }

    if (searchParams.get('endDate')) {
      initial.endDate = searchParams.get('endDate');
    }

    if (searchParams.get('startDate') || searchParams.get('endDate')) {
      initial.dateRange = 'custom';
    }

    return initial;
  }, [initialDepartmentIds, searchParams]);


  const {
    appliedFilters,
    renderFilterControls,
    setTempFiltersBatch,
  } = useTableFilters(tableFilters, initialFilterValues, { onReset: () => setSearchTerm('') });

  const resetEventPagination = useCallback(() => {
    setEvents([]);
    setEventPage(1);
    setEventHasMore(true);
  }, []);

  const handleSortChange = useCallback(
    (field) => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetEventPagination();
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [sortField, sortOrder, resetEventPagination],
  );

  useEffect(() => {
    resetEventPagination();
  }, [appliedFilters, searchTerm, activeTab, refreshKey, sortField, sortOrder, resetEventPagination]);

  const resolveDateRange = useCallback((rangeKey) => {
    if (!rangeKey) return null;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    switch (rangeKey) {
      case 'today': {
        const start = new Date(year, month, day, 0, 0, 0, 0);
        const end = new Date(year, month, day, 23, 59, 59, 999);
        return { startDate: toUtcIsoWithTime(start), endDate: toUtcIsoWithTime(end) };
      }
      case 'yesterday': {
        const start = new Date(year, month, day - 1, 0, 0, 0, 0);
        const end = new Date(year, month, day - 1, 23, 59, 59, 999);
        return { startDate: toUtcIsoWithTime(start), endDate: toUtcIsoWithTime(end) };
      }
      case 'thisMonth': {
        const start = new Date(year, month, 1, 0, 0, 0, 0);
        return { startDate: toUtcIsoWithTime(start), endDate: toUtcIsoWithTime(now) };
      }
      case 'lastMonth': {
        const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
        const end = new Date(year, month, 0, 23, 59, 59, 999);
        return { startDate: toUtcIsoWithTime(start), endDate: toUtcIsoWithTime(end) };
      }
      case 'thisYear': {
        const start = new Date(year, 0, 1, 0, 0, 0, 0);
        return { startDate: toUtcIsoWithTime(start), endDate: toUtcIsoWithTime(now) };
      }
      default:
        return null;
    }
  }, []);

  const parseDateInput = useCallback((value, endOfDay = false) => {
    if (!value) return undefined;
    const [yearRaw, monthRaw, dayRaw] = String(value).split('-');
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if (!year || !month || !day) return undefined;
    const date = endOfDay
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
    const iso = toUtcIsoWithTime(date);
    return iso || undefined;
  }, []);

  const normalizeMultiFilter = useCallback((value) => {
    if (Array.isArray(value)) {
      return value.length ? value : undefined;
    }
    return value;
  }, []);

  const buildEventParams = useCallback(
    ({
      page,
      pageSize,
    }) => {
      const statusMap = {
        planned: [4],
        inProgress: [1],
        done: [2],
        all: undefined,
      };
      const { dateRange, startDate, endDate, ...filters } = appliedFilters || {};
      const resolvedRange = resolveDateRange(dateRange);
      const resolvedStartDate = resolvedRange?.startDate ?? parseDateInput(startDate, false);
      const resolvedEndDate = resolvedRange?.endDate ?? parseDateInput(endDate, true);
      const normalizedFilters = {
        ...filters,
        systemId: normalizeMultiFilter(filters.systemId),
        eventResponsibilityId: normalizeMultiFilter(filters.eventResponsibilityId),
      };
      return {
        page,
        pageSize,
        search: searchTerm,
        ...normalizedFilters,
        startDate: resolvedStartDate,
        endDate: resolvedEndDate,
        eventStatusId: statusMap[activeTab],
        sortField: sortField || undefined,
        sortOrder: sortOrder || undefined,
      };
    },
    [
      activeTab,
      appliedFilters,
      normalizeMultiFilter,
      parseDateInput,
      resolveDateRange,
      searchTerm,
      sortField,
      sortOrder,
    ],
  );

  

  const initialEventTypeLabels = useMemo(() => parseQueryValues(searchParams, 'eventType'), [searchParams]);
  const hasInitialEventTypeQuery = initialEventTypeLabels.length > 0;
  const [initialEventTypeFilterReady, setInitialEventTypeFilterReady] = useState(!hasInitialEventTypeQuery);
  const initialEventTypeFilterAppliedRef = useRef(false);
  const initialEventTypeIds = useMemo(() => {
    if (!hasInitialEventTypeQuery || !eventTypeOptions.length) {
      return [];
    }

    return initialEventTypeLabels
      .map((label) => {
        const normalizedLabel = String(label).trim().toLowerCase();
        const matchedOption = eventTypeOptions.find(
          (option) => String(option.label || '').trim().toLowerCase() === normalizedLabel,
        );
        if (matchedOption) {
          return matchedOption.value;
        }
        const numericValue = Number(label);
        return Number.isFinite(numericValue) ? numericValue : null;
      })
      .filter((value) => value !== null && value !== undefined);
  }, [eventTypeOptions, hasInitialEventTypeQuery, initialEventTypeLabels]);

  useEffect(() => {
    if (
      !hasInitialEventTypeQuery ||
      initialEventTypeFilterReady ||
      initialEventTypeFilterAppliedRef.current ||
      !eventTypeOptions.length
    ) {
      return;
    }

    initialEventTypeFilterAppliedRef.current = true;
    if (initialEventTypeIds.length) {
      setTempFiltersBatch({ eventTypeId: initialEventTypeIds }, { apply: true });
    }
    setInitialEventTypeFilterReady(true);
  }, [
    hasInitialEventTypeQuery,
    initialEventTypeFilterReady,
    initialEventTypeIds,
    eventTypeOptions.length,
  ]);


  useEffect(() => {
    if (!initialEventTypeFilterReady) {
      return;
    }

    const fetchEvents = async () => {
      setLoading(true);
      try {
        const firstPageSize = 25;
        const subsequentPageSize = 5;
        const pageSize = eventPage === 1 ? firstPageSize : subsequentPageSize;
        const page = eventPage === 1 ? 1 : eventPage + (firstPageSize / subsequentPageSize) - 1;
        const params = buildEventParams({ page, pageSize });
        const res = await getPaginatedEvents(params);
        const rows = res.data || [];
        setEvents(prev => {
          const existingIds = new Set(prev.map(e => e.id));
          const newItems = rows.filter(e => !existingIds.has(e.id));
          const updated = [...prev, ...newItems];
          setEventHasMore(updated.length < res.total);
          return updated;
        });
      } catch (err) {
        console.error('Error loading events', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();

  }, [eventPage, appliedFilters, searchTerm, activeTab, refreshKey, sortField, sortOrder, initialEventTypeFilterReady]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        eventHasMore &&
        !loading
      ) {
        setEventPage(p => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [eventHasMore, loading]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deleteEvents(id);
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error('Error deleting event', err);
    }
  };

  const mapAssetsToOptions = useCallback((assets = []) =>
    assets.map((asset) => {
      const parts = [asset.assetNumber || asset.unitNumber, asset.assetDescription].filter(Boolean);
      const label = parts.length ? parts.join(' - ') : `Asset ${asset.id}`;
      return { value: asset.id, label };
    }),
    []
  );

  const loadInitialAssets = useCallback(
    async (selectedOption = null) => {
      try {
        const res = await getPaginatedAssets({ page: 1, pageSize: 10 });
        const rows = res.data || [];
        let options = mapAssetsToOptions(rows);
        if (selectedOption) {
          const exists = options.some((opt) => opt.value === selectedOption.value);
          options = exists
            ? options.map((opt) => (opt.value === selectedOption.value ? selectedOption : opt))
            : [selectedOption, ...options];
        }
        setAssetOptions(options);
        return options;
      } catch (err) {
        console.error('Error loading assets', err);
        if (selectedOption) {
          setAssetOptions([selectedOption]);
          return [selectedOption];
        }
        setAssetOptions([]);
        return [];
      }
    },
    [mapAssetsToOptions]
  );

  const loadEventDelays = async (eventId) => {
    if (!eventId) {
      setEventDelays([]);
      return;
    }
    try {
      const resp = await getEventDelayByEventId(eventId);
      const delays = resp.data || resp || [];
      setEventDelays(delays);
    } catch (err) {
      console.error('Error loading delays', err);
      setEventDelays([]);
    }
  };

  const handleShowComments = async (event) => {
    const eventId = event?.id;
    if (!eventId) return;
    setCommentsEvent(event);
    setShowCommentsModal(true);
    setCommentsLoading(true);
    try {
      const response = await getEventCommentsByEventId(eventId);
      const comments = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setCommentsHistory(normalizeCommentList(comments));
    } catch (err) {
      console.error('Error loading event comments', err);
      setCommentsHistory([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleShowDelays = async (event) => {
    const eventId = event?.id;
    if (!eventId) return;
    setDelaysEvent(event);
    setShowDelaysListModal(true);
    setDelaysListLoading(true);
    try {
      const response = await getEventDelayByEventId(eventId);
      const delays = response?.data || response || [];
      setDelaysHistory(Array.isArray(delays) ? delays : []);
    } catch (err) {
      console.error('Error loading event delays', err);
      setDelaysHistory([]);
    } finally {
      setDelaysListLoading(false);
    }
  };

  const loadWorkOrdersForEvent = async (event) => {
    if (!event) {
      setAvailableWorkOrders([]);
      setAssignedWorkOrders([]);
      return;
    }

    try {
      const resp = await getPaginatedWorkOrders({
        page: 1,
        pageSize: 1000,
        assetId: [event.assetId],
      });
      const data = resp.data || resp || [];
      const available = data.filter((wo) => wo.eventId === null);
      const assigned = data.filter((wo) => wo.eventId === event.id && wo.eventId !== null);
      setAvailableWorkOrders(available);
      setAssignedWorkOrders(assigned);
      setEvents((prev) =>
        prev.map((e) =>
          e.id === event.id ? { ...e, workOrderCount: assigned.length } : e
        )
      );
    } catch (err) {
      console.error('Error loading work orders', err);
      setAvailableWorkOrders([]);
      setAssignedWorkOrders([]);
    }
  };

  const buildWorkOrderDefaults = useCallback(
    (eventData) => {
      const statusId = mapEventStatusToWorkOrderStatus(eventData?.eventStatus);
      return {
        workOrderTitle: '',
        workOrderDescription: '',
        scheduledAt: eventData?.plannedStartDate ?? null,
        dueDate: eventData?.plannedEndDate ?? null,
        startedAt: eventData?.startDate ?? null,
        completedAt: eventData?.endDate ?? null,
        statusId,
        taskTypeId: 4,
        priorityId: 3,
        statutory: 0,
        breakdown: Number(eventData?.eventTypeId) === 2 ? 1 : 0,
        workorderFiles: [],
        workOrderImage: [],
        completionImages: [],
        costResponsibilityId: 1,
        cycleGroupOrder: null,
        assetId: eventData?.assetId ?? null,
      };
    },
    [mapEventStatusToWorkOrderStatus],
  );

  const openAddWorkOrderModal = useCallback(() => {
    const defaults = buildWorkOrderDefaults(newEvent);
    setNewWorkOrder(defaults);
    setWorkOrderErrors({});
    setShowAddWorkOrderModal(true);
  }, [buildWorkOrderDefaults, newEvent, setWorkOrderErrors]);

  useEffect(() => {
    if (!showAddWorkOrderModal) {
      setWorkOrderAssetOption(null);
      return;
    }

    if (!newWorkOrder?.assetId) {
      setWorkOrderAssetOption(null);
      return;
    }

    setWorkOrderAssetOption((current) => {
      if (current && String(current.value) === String(newWorkOrder.assetId)) {
        return current;
      }

      const fromOptions = assetOptions.find(
        (opt) => String(opt.value) === String(newWorkOrder.assetId)
      );
      if (fromOptions) {
        return fromOptions;
      }

      const editingMatch =
        editingEvent && String(editingEvent.assetId) === String(newWorkOrder.assetId)
          ? [
            editingEvent?.asset?.assetNumber || editingEvent?.assetNumber,
            editingEvent?.asset?.assetDescription || editingEvent?.assetDescription,
          ]
            .filter(Boolean)
            .join(' – ')
          : null;

      const label =
        newEvent?.assetLabel ||
        [newEvent?.assetNumber, newEvent?.assetDescription]
          .filter(Boolean)
          .join(' – ') ||
        editingMatch ||
        String(newWorkOrder.assetId);

      return { value: newWorkOrder.assetId, label };
    });
  }, [assetOptions, editingEvent, newEvent, newWorkOrder?.assetId, showAddWorkOrderModal]);

  useEffect(() => {
    if (!showModal || editingEvent) {
      return;
    }

    if (!newEvent?.assetId) {
      // setAvailableWorkOrders([]);
      setAssignedWorkOrders([]);
      return;
    }

    loadWorkOrdersForEvent({ assetId: newEvent.assetId, id: null });
  }, [newEvent?.assetId, showModal]);

  const openModal = async (event = null) => {
    setEditingEvent(event);
    setNewEventErrors({});
    setConflictModal(null);
    if (event) {
      setEventComments([]);
      setNewEvent({
        name: event.name,
        eventTypeId: event.eventTypeId,
        eventStatus: event?.eventStatus || '',
        assetId: event.assetId,
        plannedStartDate: toLocalInputFormat(event.plannedStartDate),
        plannedEndDate: toLocalInputFormat(event.plannedEndDate),
        startDate: toLocalInputFormat(event.startDate),
        endDate: toLocalInputFormat(event.endDate),
        systemId: event?.systemId ?? null,
        eventLocationId:
          event?.eventLocationId ?? event?.eventLocation?.id ?? null,
        eventResponsibilityId:
          event?.eventResponsibilityId ?? event?.eventResponsibility?.id ?? null,
      });
      const selectedAssetOption =
        event.assetId
          ? {
            value: event.assetId,
            label:
              [event.asset?.assetNumber || event.asset?.unitNumber, event.asset?.assetDescription]
                .filter(Boolean)
                .join(' - ') || `Asset ${event.assetId}`,
          }
          : null;
      await loadInitialAssets(selectedAssetOption);
      await loadWorkOrdersForEvent(event);
      await loadEventDelays(event.id);
    } else {
      setNewEvent({
        eventResponsibilityId: defaultEventResponsibilityId ?? null,
      });
      setAssignedWorkOrders([]);
      setAvailableWorkOrders([]);
      setEventDelays([]);
      setEventComments([]);
      await loadInitialAssets();
    }
    setShowModal(true);
  };

  useEffect(() => {
    if (!showModal || editingEvent || !defaultEventResponsibilityId) {
      return;
    }
    setNewEvent((prev) => {
      if (prev?.eventResponsibilityId) return prev;
      return {
        ...prev,
        eventResponsibilityId: defaultEventResponsibilityId,
      };
    });
  }, [defaultEventResponsibilityId, editingEvent, showModal]);

  const firstAssignedWorkOrder = useMemo(() => {
    if (!Array.isArray(assignedWorkOrders) || assignedWorkOrders.length === 0) {
      return null;
    }
    if (editingEvent?.id) {
      const match = assignedWorkOrders.find(
        (wo) => String(wo.eventId) === String(editingEvent.id)
      );
      if (match) return match;
    }
    return assignedWorkOrders[0];
  }, [assignedWorkOrders, editingEvent?.id]);

  const normalizeCrewArray = (value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'object') {
          if (entry.userId !== undefined && entry.userId !== null) {
            return entry.userId;
          }
          if (entry.id !== undefined && entry.id !== null) {
            return entry.id;
          }
        }
        return entry;
      })
      .filter((id) => id !== null && id !== undefined);
  };

  const crewsMatch = (a, b) => {
    const normA = [...new Set(normalizeCrewArray(a))].sort();
    const normB = [...new Set(normalizeCrewArray(b))].sort();
    if (normA.length !== normB.length) return false;
    return normA.every((val, index) => val === normB[index]);
  };

  const handleSave = async () => {
    const isValid = validateNewEvent();

    if (!isValid) return;
    const eventStatus = Number(newEvent?.eventStatus);
    const { eventStart, eventEnd } = getEventRange(newEvent);
    if (eventStatus === 2 && completeEventWorkOrdersSetting === true) {
      const canComplete = areWorkOrdersCompletableForEvent(
        assignedWorkOrders,
        eventStart,
        eventEnd,
      );
      if (!canComplete) {
        await showEventDialog({
          title: 'Complete Event',
          message:
            'All event Work Orders must have status Complete, Cancelled or otherwise removed from the event to complete the event.',
          confirmText: 'OK',
          showCancel: false,
        });
        return;
      }
    }

    if (eventStatus === 2) {
      const hasOpenDelay = (Array.isArray(eventDelays) ? eventDelays : []).some(
        (delay) => delay?.delayStartDate && !delay?.delayEndDate,
      );
      if (hasOpenDelay) {
        window.alert('All Delays must have an end date to complete the event.');
        return;
      }
    }
    const workOrderIds = Array.from(
      new Set(
        assignedWorkOrders
          .map((wo) => Number(wo?.id))
          .filter((id) => Number.isFinite(id)),
      ),
    );
    const normalizedEventStatus = Number(newEvent.eventStatus) || null;
    const normalizedEndDate =
      normalizedEventStatus === 1
        ? null
        : newEvent.endDate
          ? new Date(newEvent.endDate).toISOString()
          : null;

    const payload = {
      name: newEvent.name,
      assetId: Number(newEvent.assetId),
      eventTypeId: Number(newEvent.eventTypeId),
      plannedStartDate: newEvent.plannedStartDate ? new Date(newEvent.plannedStartDate).toISOString() : null,
      plannedEndDate: newEvent.plannedEndDate ? new Date(newEvent.plannedEndDate).toISOString() : null,
      startDate: newEvent.startDate ? new Date(newEvent.startDate).toISOString() : null,
      endDate: normalizedEndDate,
      eventStatus: normalizedEventStatus,
      systemId:
        newEvent.systemId !== undefined && newEvent.systemId !== null && newEvent.systemId !== ''
          ? Number(newEvent.systemId)
          : null,
      eventLocationId:
        newEvent.eventLocationId !== undefined &&
          newEvent.eventLocationId !== null &&
          newEvent.eventLocationId !== ''
          ? Number(newEvent.eventLocationId)
          : null,
      eventResponsibilityId:
        newEvent.eventResponsibilityId !== undefined &&
          newEvent.eventResponsibilityId !== null &&
          newEvent.eventResponsibilityId !== ''
          ? Number(newEvent.eventResponsibilityId)
          : null,
      workOrderIds,
    };
    const pendingComments = !editingEvent && Array.isArray(eventComments) ? eventComments : [];
    const pendingDelays = !editingEvent && Array.isArray(eventDelays) ? eventDelays : [];
    const hasOutsideRange = hasWorkOrdersOutsideEventRange(assignedWorkOrders, eventStart, eventEnd);

    let alignedUpdates = [];

    if (hasOutsideRange) {
      const shouldAlignDates = await showEventDialog({
        title: 'Align Work Order Dates',
        message:
          'One or more Work Order start and end dates falls outside the event start / end date. Do you want to align these work orders to the event start and end date / time?',
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: true,
      });

      if (!shouldAlignDates) {
        return;
      }

      alignedUpdates = assignedWorkOrders
        .map((wo) => ({
          workOrder: wo,
          payload: buildAlignmentPayload(wo, eventStart, eventEnd),
        }))
        .filter(({ payload }) => Object.keys(payload).length > 0);
    }

    try {
      if (editingEvent) {
        await updateEvents(editingEvent.id, payload);
      } else {
        const created = await createEvents(payload);
        const createdId = created?.id;
        if (createdId) {
          if (pendingComments.length) {
            const commentPayloads = pendingComments
              .map((comment) => {
                const comments = (comment?.comments || comment?.comment || '').trim();
                const photos = Array.isArray(comment?.photos) ? comment.photos : [];
                if (!comments && photos.length === 0) return null;
                const userId =
                  comment?.userId ?? comment?.user?.id ?? comment?.user?.userId ?? userProfile?.id ?? null;
                if (!userId) return null;
                return {
                  eventsId: createdId,
                  comments,
                  photos,
                  userId,
                };
              })
              .filter(Boolean);
            await Promise.all(commentPayloads.map((data) => createEventComment(data)));
          }
          if (pendingDelays.length) {
            const delayPayloads = pendingDelays
              .map((delay) => {
                const eventDelayCodeId = delay?.eventDelayCodeId ?? delay?.eventDelayCode?.id ?? null;
                if (!eventDelayCodeId) return null;
                return {
                  eventsId: createdId,
                  eventDelayCodeId,
                  delayStartDate: delay?.delayStartDate ?? null,
                  delayEndDate: delay?.delayEndDate ?? null,
                };
              })
              .filter(Boolean);
            await Promise.all(delayPayloads.map((data) => createEventDelay(data)));
          }
        }
      }

      if (alignedUpdates.length) {
        await Promise.all(
          alignedUpdates.map(({ workOrder, payload }) => updateWorkOrder(workOrder.id, payload)),
        );
        setAssignedWorkOrders((prev) =>
          prev.map((wo) => {
            const match = alignedUpdates.find(({ workOrder }) => workOrder.id === wo.id);
            return match ? { ...wo, ...match.payload } : wo;
          }),
        );
      }

      if (eventTechsEnabled && firstAssignedWorkOrder) {
        const selectedCrew = normalizeCrewArray(newEvent?.crew);
        const existingCrew = normalizeCrewArray(firstAssignedWorkOrder.crew);
        if (!crewsMatch(selectedCrew, existingCrew)) {
          const uniqueCrew = [...new Set(selectedCrew)];
          await updateWorkOrder(firstAssignedWorkOrder.id, { crew: uniqueCrew });
          setAssignedWorkOrders((prev) =>
            prev.map((wo) =>
              wo.id === firstAssignedWorkOrder.id ? { ...wo, crew: uniqueCrew } : wo
            )
          );
        }
      }

      setShowModal(false);
      setConflictModal(null);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      const responseData = err?.response?.data;
      if (responseData?.error === 'EVENT_DATE_OVERLAP') {
        const conflicts = Array.isArray(responseData?.conflicts)
          ? responseData.conflicts
          : [];
        setConflictModal({
          message:
            responseData?.message ||
            'This event overlaps with other events on the same asset.',
          conflicts,
        });
        return;
      }
      console.error('Error saving event', err);
    }
  };

  const handleExportExcel = async () => {
    const headers = [
      'Name',
      'Status',
      'Responsibility',
      'Asset',
      'Planned Start',
      'Planned End',
      'Planned Event Duration',
      'Start',
      'End',
      'Actual Event Duration',
      'Delays',
      'Delay Total',
      'Comments',
    ];
    let rows = [];
    try {
      const params = buildEventParams({ page: 1, pageSize: 1000 });
      const res = await getPaginatedEvents(params);
      const exportEvents = res.data || [];
      rows = exportEvents.map((event) => {
        const commentEntries = event.tblEventComments || event.eventComments || [];
        const commentText = Array.isArray(commentEntries)
          ? commentEntries
            .map((comment) => (comment?.comments || '').trim())
            .filter(Boolean)
            .join('\n')
          : '';
        return [
          event.name || '',
          event.eventStatus2?.name || event.eventStatus?.name || '',
          event.eventResponsibility?.name || '',
          event.asset ? `${event.asset.assetNumber || ''} - ${event.asset.assetDescription || ''}` : '',
          event.plannedStartDate ? formatIntDateTime(event.plannedStartDate) : '',
          event.plannedEndDate ? formatIntDateTime(event.plannedEndDate) : '',
          formatDurationHHMM(event.plannedStartDate, event.plannedEndDate),
          event.startDate ? formatIntDateTime(event.startDate) : '',
          event.endDate ? formatIntDateTime(event.endDate) : '',
          formatDurationHHMM(event.startDate, event.endDate),
          formatDelayList(event.tblEventDelays || event.eventDelays || []),
          formatDelayTotalHHMM(event.tblEventDelays || event.eventDelays || []),
          commentText,
        ];
      });
    } catch (err) {
      console.error('Error exporting events', err);
      return;
    }
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Events');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'events.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    try {
      const params = {
        ...buildEventParams({ page: 1, pageSize: 1000 }),
        eventStatus: activeTab,
      };
      const pdfData = await exportEventsPdfReport(params);
      const blob = pdfData instanceof Blob
        ? pdfData
        : new Blob([pdfData], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'events-report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting events PDF', err);
    }
  };

  const handleDelaySave = async () => {
    const isBaseValid = validateDelayForm();
    const customErrors = validateDelayDateRange(
      delayForm.delayStartDate,
      delayForm.delayEndDate,
      newEvent,
      editingEvent,
    );
    if (Object.keys(customErrors).length) {
      setDelayErrors((prev) => ({ ...prev, ...customErrors }));
    }
    if (!isBaseValid || Object.keys(customErrors).length) return;

    const eventStart = newEvent.startDate
      ? new Date(newEvent.startDate)
      : new Date(newEvent.plannedStartDate);
    const eventEnd = newEvent.endDate
      ? new Date(newEvent.endDate)
      : new Date(newEvent.plannedEndDate);
    const delayStart = delayForm.delayStartDate ? new Date(delayForm.delayStartDate) : null;
    const delayEnd = delayForm.delayEndDate ? new Date(delayForm.delayEndDate) : null;

    const durationHours = Math.abs(eventEnd - eventStart) / (1000 * 60 * 60);
    const existingTotal = eventDelays.reduce((sum, d) => sum + calcDelayDuration(d), 0);
    const updatedTotal = editingDelay
      ? existingTotal - calcDelayDuration(editingDelay) + delayDuration
      : existingTotal + delayDuration;
    if (Number.isFinite(durationHours) && updatedTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return;
    }

    const payload = {
      eventDelayCodeId: Number(delayForm.eventDelayCodeId),
      delayStartDate: delayStart ? delayStart.toISOString() : null,
      delayEndDate: delayEnd ? delayEnd.toISOString() : null,
    };
    try {
      if (editingEvent?.id) {
        const remotePayload = { ...payload, eventsId: editingEvent.id };
        if (editingDelay) {
          await updateEventDelay(editingDelay.id, remotePayload);
        } else {
          await createEventDelay(remotePayload);
        }
        await loadEventDelays(editingEvent.id);
        setShowDelayModal(false);
      } else {
        const delayCode = delayCodes.find(
          (code) => String(code.id) === String(payload.eventDelayCodeId),
        );
        setEventDelays((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const updatedDelay = {
            ...editingDelay,
            id: editingDelay?.id ?? Date.now(),
            ...payload,
            eventDelayCode: delayCode || editingDelay?.eventDelayCode || null,
          };
          const next = editingDelay
            ? existing.map((entry) => (entry.id === editingDelay.id ? updatedDelay : entry))
            : [updatedDelay, ...existing];
          return next;
        });
        setShowDelayModal(false);
      }
    } catch (err) {
      console.error('Error saving delay', err);
    }
  };

  const handleEditDelay = (delay) => {
    setEditingDelay(delay);
    setDelayForm({
      eventDelayCodeId: delay.eventDelayCodeId,
      delayStartDate: toLocalInputFormat(delay.delayStartDate),
      delayEndDate: toLocalInputFormat(delay.delayEndDate),
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleAddDelay = () => {
    setEditingDelay(null);
    const defaultStartDate = computeDefaultDelayStartDate(newEvent, editingEvent);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: defaultStartDate ? toLocalInputFormat(defaultStartDate, true) : '',
      delayEndDate: '',
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleDeleteDelay = async (id) => {
    if (!window.confirm('Delete delay?')) return;
    try {
      if (editingEvent?.id) {
        await deleteEventDelay(id);
        await loadEventDelays(editingEvent.id);
      } else {
        setEventDelays((prev) => (Array.isArray(prev) ? prev.filter((d) => d.id !== id) : []));
      }
    } catch (err) {
      console.error('Error deleting delay', err);
    }
  };

  const openAssignModal = () => {
    const unassigned = availableWorkOrders.filter(
      (wo) => !wo.eventId && ['open', 'in progress'].includes((wo.status?.name || '').toLowerCase())
    );
    setAssignOptions(unassigned);
    setAssignSelected([]);
    setAssignSearch('');
    setShowAssignModal(true);
  };

  const handleAssignSelected = async () => {
    if (!assignSelected.length) {
      setShowAssignModal(false);
      return;
    }

    const newlySelected = assignOptions.filter((wo) => assignSelected.includes(wo.id));
    const eventId = editingEvent?.id;

    try {
      if (eventId) {
        await Promise.all(
          assignSelected.map((id) => updateWorkOrder(id, { eventId }))
        );
      }
      setAssignedWorkOrders((prev) => {
        const existingIds = new Set(prev.map((wo) => wo.id));
        const additions = newlySelected.filter((wo) => !existingIds.has(wo.id));
        const mappedAdditions = additions.map((wo) => ({ ...wo, eventId: eventId ?? wo.eventId ?? null }));
        return [...prev, ...mappedAdditions];
      });
      setAvailableWorkOrders((prev) => prev.filter((wo) => !assignSelected.includes(wo.id)));
      if (eventId) {
        setEvents((prev) =>
          prev.map((e) =>
            e.id === eventId
              ? { ...e, workOrderCount: (e.workOrderCount || 0) + newlySelected.length }
              : e
          )
        );
      }
      setShowAssignModal(false);
      setAssignOptions([]);
      setAssignSelected([]);
    } catch (err) {
      console.error('Error assigning work orders', err);
    }
  };

  const handleSelectAllAssign = () => {
    setAssignSelected(assignOptions.map((wo) => wo.id));
  };

  const handleDeselectAllAssign = () => {
    setAssignSelected([]);
  };

  const handleDeleteWo = async (id) => {
    if (!window.confirm('Remove work order from event?')) return;
    const eventId = editingEvent?.id;
    try {
      if (eventId) {
        await updateWorkOrder(id, { eventId: null });
      }
      const removed = assignedWorkOrders.find((wo) => wo.id === id);
      setAssignedWorkOrders((prev) => prev.filter((wo) => wo.id !== id));
      if (removed) {
        setAvailableWorkOrders((prev) => [...prev, { ...removed, eventId: null }]);
      }
      if (eventId) {
        setEvents((prev) =>
          prev.map((e) =>
            e.id === eventId
              ? { ...e, workOrderCount: Math.max(0, (e.workOrderCount || 1) - 1) }
              : e
          )
        );
      }
    } catch (err) {
      console.error('Error removing work order', err);
    }
  };

  const handleWoStatusChange = async (id, statusId) => {
    try {
      await updateWorkOrder(id, { statusId });
      const statusObj = workOrderStatuses.find((s) => String(s.value) === String(statusId));
      const status = statusObj
        ? {
          id: statusObj.value,
          name: statusObj.label,
          colours: { bootstrapName: statusObj.bootstrapName },
        }
        : null;
      setAssignedWorkOrders((prev) =>
        prev.map((wo) => (wo.id === id ? { ...wo, statusId: Number(statusId), status } : wo))
      );
    } catch (err) {
      console.error('Error updating work order status', err);
    }
  };

  const handleSaveAddWorkOrder = async () => {
    if (!validateWorkOrder()) return;
    const eventId = editingEvent?.id;
    if (!eventId) return;
    setWorkOrderModalLoading(true);
    try {
      const payload = {
        ...newWorkOrder,
        assetId: newWorkOrder.assetId ? Number(newWorkOrder.assetId) : null,
        statusId: Number(newWorkOrder.statusId),
        taskTypeId: Number(newWorkOrder.taskTypeId),
        priorityId: Number(newWorkOrder.priorityId),
        breakdown: Number(newWorkOrder.breakdown ?? 0),
        statutory: Number(newWorkOrder.statutory ?? 0),
        scheduledAt: newWorkOrder.scheduledAt ? toUtcIsoWithTime(newWorkOrder.scheduledAt) : null,
        dueDate: newWorkOrder.dueDate ? toUtcIsoWithTime(newWorkOrder.dueDate) : null,
        startedAt: newWorkOrder.startedAt ? toUtcIsoWithTime(newWorkOrder.startedAt) : null,
        completedAt: newWorkOrder.completedAt ? toUtcIsoWithTime(newWorkOrder.completedAt) : null,
        eventId,
      };

      const created = await createWorkOrder(payload);
      const workOrderId = created?.id || created?.data?.id;
      const createdWorkOrder = workOrderId ? await getWorkOrderById(workOrderId) : created?.data || created;

      if (createdWorkOrder) {
        setAssignedWorkOrders((prev) => [...prev, createdWorkOrder]);
        setEvents((prev) =>
          prev.map((e) =>
            e.id === eventId
              ? { ...e, workOrderCount: (e.workOrderCount || 0) + 1 }
              : e
          )
        );
      }

      setShowAddWorkOrderModal(false);
    } catch (err) {
      console.error('Error saving work order', err);
    } finally {
      setWorkOrderModalLoading(false);
    }
  };

  const handleCompleteAllWork = async () => {
    const completeStatus = workOrderStatuses.find(
      (s) => s.label && s.label.toLowerCase() === 'complete'
    );
    if (!completeStatus) return;
    const { eventStart, eventEnd } = getEventRange(newEvent);
    const hasOutsideRange = hasWorkOrdersOutsideEventRange(assignedWorkOrders, eventStart, eventEnd);
    if (hasOutsideRange) {
      const shouldAlignDates = await showEventDialog({
        title: 'Align Work Order Dates',
        message:
          'One or more Work Order start and end dates falls outside the event start / end date. Do you want to align these work orders to the event start and end date / time?',
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: true,
      });
      if (!shouldAlignDates) {
        return;
      }
    }
    try {
      await Promise.all(
        assignedWorkOrders.map((wo) =>
          updateWorkOrder(
            wo.id,
            buildCompleteWorkOrderPayload(
              wo,
              completeStatus.value,
              eventStart,
              eventEnd,
              hasOutsideRange,
            ),
          ),
        ),
      );
      setAssignedWorkOrders((prev) =>
        prev.map((wo) => ({
          ...wo,
          statusId: completeStatus.value,
          ...buildCompleteWorkOrderPayload(
            wo,
            completeStatus.value,
            eventStart,
            eventEnd,
            hasOutsideRange,
          ),
          status: {
            id: completeStatus.value,
            name: completeStatus.label,
            colours: { bootstrapName: completeStatus.bootstrapName },
          },
        }))
      );
    } catch (err) {
      console.error('Error completing work orders', err);
    }
  };

  const openEditWoModal = (wo) => {
    setEditingWo(wo);
    setEditWoForm({
      statusId: wo.statusId || '',
      startedAt: toLocalInputFormat(
        wo.startedAt || newEvent.startDate || newEvent.plannedStartDate
      ),
      completedAt: toLocalInputFormat(
        wo.completedAt || newEvent.endDate || newEvent.plannedEndDate
      ),
      failureId: wo.failureId || '',
      causeId: wo.causeId || '',
      symptom: wo.symptom || '',
      notes: wo.notes || '',
    });
    setEditWoErrors({});
    setShowEditWoModal(true);
  };

  const handleSaveEditWo = async () => {
    const statusName = workOrderStatuses.find(
      (s) => String(s.value) === String(editWoForm.statusId)
    )?.label?.toLowerCase();
    const errs = {};
    if (!editWoForm.statusId) errs.statusId = 'Required';
    if (!editWoForm.startedAt) errs.startedAt = 'Required';
    if (statusName === 'complete' && !editWoForm.completedAt) errs.completedAt = 'Required';
    setEditWoErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const payload = {
      statusId: Number(editWoForm.statusId),
      startedAt: editWoForm.startedAt ? new Date(editWoForm.startedAt).toISOString() : null,
      completedAt: editWoForm.completedAt
        ? new Date(editWoForm.completedAt).toISOString()
        : null,
      failureId: editWoForm.failureId ? Number(editWoForm.failureId) : null,
      causeId: editWoForm.causeId ? Number(editWoForm.causeId) : null,
      symptom: editWoForm.symptom || '',
      notes: editWoForm.notes || '',
    };
    try {
      await updateWorkOrder(editingWo.id, payload);
      const statusOption = workOrderStatuses.find(
        (s) => String(s.value) === String(payload.statusId)
      );
      const status = statusOption
        ? {
          id: statusOption.value,
          name: statusOption.label,
          colours: { bootstrapName: statusOption.bootstrapName },
        }
        : null;
      setAssignedWorkOrders((prev) =>
        prev.map((wo) =>
          wo.id === editingWo.id
            ? {
              ...wo,
              ...payload,
              status,
            }
            : wo
        )
      );
      setShowEditWoModal(false);
    } catch (err) {
      console.error('Error saving work order', err);
    }
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm me-2"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex flex-wrap align-items-top gap-2">
            {renderFilterControls()}
          </div>
          <div className="ms-auto d-flex align-items-top gap-2 ms-auto">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>Excel</button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportPdf}>PDF</button>
                </li>
              </ul>
            </div>
            {userProfile?.privileges?.includes('events:create') && (
              <div className="dropdown me-2">
                <button
                  className="btn btn-primary text-sm btn-sm px-12 py-12 radius-4"
                  style={{ width: "118px" }}
                  onClick={() => openModal(null)}
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Add Event
                </button>
              </div>
            )}
          </div>

        </div>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-16">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  All Events
                </button>
              </li>              
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'planned' ? 'active' : ''}`}
                  onClick={() => setActiveTab('planned')}
                >
                  Pending
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'inProgress' ? 'active' : ''}`}
                  onClick={() => setActiveTab('inProgress')}
                >
                  In Progress
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'done' ? 'active' : ''}`}
                  onClick={() => setActiveTab('done')}
                >
                  Complete
                </button>
              </li>
            </ul>
          </div>
          <div>
            {isShiftLogModuleEnabled && canViewShiftLog && (
              <button
                className="btn btn-outline-primary me-24 rounded-1"
                type="button"
                onClick={() => setShowShiftLogManager(true)}
              >
                Open Shift Log
              </button>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <div
            ref={listContainerRef}
            className="paginated-table-container scroll-sm"
            style={{ height: 'calc(100vh - 285px)' }}
          >
            <EventDataTable
              events={events}
              onEdit={(e) => openModal(e)}
              onDelete={handleDelete}
              onShowComments={handleShowComments}
              onShowDelays={handleShowDelays}
              loading={loading}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSortChange}
              hiddenColumns={hiddenColumns}
              activeTab={activeTab}
            />
          </div>
        </div>
        <EventEditModal
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setConflictModal(null);
          }}
          onSave={handleSave}
          editingEvent={editingEvent}
          newEvent={newEvent}
          setNewEvent={setNewEvent}
          newEventErrors={newEventErrors}
          statusOptions={statusOptions}
          eventTypeOptions={eventTypeOptions}
          eventResponsibilityOptions={eventResponsibilityOptions}
          showEventResponsibility
          assetOptions={assetOptions}
          handleAssetSearch={handleAssetSearch}
          assignedWorkOrders={assignedWorkOrders}
          workOrderStatuses={workOrderStatuses}
          onCompleteAllWork={handleCompleteAllWork}
          onOpenAssignModal={openAssignModal}
          onOpenAddWorkOrder={openAddWorkOrderModal}
          onWorkOrderStatusChange={handleWoStatusChange}
          onDeleteWorkOrder={handleDeleteWo}
          onEditWorkOrder={openEditWoModal}
          eventDelays={eventDelays}
          onAddDelay={handleAddDelay}
          onEditDelay={handleEditDelay}
          onDeleteDelay={handleDeleteDelay}
          formatDateTime={formatDateTime}
          showSystem={showSystem}
          eventComments={eventComments}
          onEventCommentsChange={setEventComments}
        />
        <WorkOrderEditModal
          showModal={showAddWorkOrderModal}
          editingId={null}
          newWorkOrder={newWorkOrder}
          modalLoading={workOrderModalLoading}
          generalTabHasError={Object.keys(workOrderErrors).length > 0}
          showCreateEventToggle={false}
          onClose={() => setShowAddWorkOrderModal(false)}
          onCreateEventToggle={() => { }}
          workOrderGeneralFields={workOrderGeneralFields}
          renderGeneralFields={renderWorkOrderField}
          duration=""
          onDurationChange={() => { }}
          combinedErrors={workOrderErrors}
          onDurationBlur={() => { }}
          actualDuration=""
          onActualDurationChange={() => { }}
          onActualDurationBlur={() => { }}
          workOrderCompleteFields={[]}
          renderCompleteFields={() => null}
          renderProgressFields={() => null}
          partsCostTotal={0}
          actualPartsCost=""
          onActualPartsCostChange={() => { }}
          labourCostTotal={0}
          actualLabourCost=""
          onActualLabourCostChange={() => { }}
          servicesCostTotal={0}
          actualMiscCost=""
          onActualMiscCostChange={() => { }}
          labourHoursTotal={0}
          actualLabourHours=""
          onActualLabourHoursChange={() => { }}
          workOrderSchedule={null}
          scheduleFields={[]}
          renderScheduleField={() => null}
          workOrderParts={[]}
          setWorkOrderParts={() => { }}
          deletedPartIds={[]}
          setDeletedPartIds={() => { }}
          userProfile={userProfile}
          expenseOptions={[]}
          defaultPartsExpenseCode={null}
          workOrderLabour={[]}
          setWorkOrderLabour={() => { }}
          deletedLabourIds={[]}
          setDeletedLabourIds={() => { }}
          tradeOptions={[]}
          defaultLabourExpenseCode={null}
          setLabourForm={() => { }}
          setEditingLabourIndex={() => { }}
          setLabourErrors={() => { }}
          setShowLabourModal={() => { }}
          isWorkOrderLocked={false}
          workOrderServices={[]}
          setWorkOrderServices={() => { }}
          setDeletedServiceIds={() => { }}
          defaultServiceExpenseCode={null}
          serviceOptions={[]}
          workOrderEquipment={[]}
          setWorkOrderEquipment={() => { }}
          deletedEquipmentIds={[]}
          setDeletedEquipmentIds={() => { }}
          setEditingEquipmentIndex={() => { }}
          setEquipmentForm={() => { }}
          setEquipmentErrors={() => { }}
          setShowEquipmentModal={() => { }}
          workOrderForms={[]}
          onViewForm={() => { }}
          onPrintForm={() => { }}
          onDeleteForm={() => { }}
          onOpenAddFormModal={() => { }}
          workOrderDocumentFields={[]}
          renderDocumentFields={() => null}
          onFileDelete={() => { }}
          handleSave={handleSaveAddWorkOrder}
          showEquipmentModal={false}
          editingEquipmentIndex={null}
          equipmentForm={{}}
          equipmentErrors={{}}
          clearEquipmentFieldError={() => { }}
          onCloseEquipmentModal={() => { }}
          onSaveEquipment={() => { }}
          showLabourModal={false}
          editingLabourIndex={null}
          labourForm={{}}
          labourErrors={{}}
          clearLabourFieldError={() => { }}
          onCloseLabourModal={() => { }}
          onSaveLabour={() => { }}
          showFormModal={false}
          selectedFormId={null}
          formOptions={[]}
          onSelectedFormChange={() => { }}
          onCloseFormModal={() => { }}
          onSaveFormSelection={() => { }}
        />
        <ShiftLogManager
          show={showShiftLogManager}
          onClose={() => setShowShiftLogManager(false)}
          userProfile={userProfile}
        />
        {conflictModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Conflicting Events</h5>
                  <button className="btn-close" onClick={() => setConflictModal(null)} />
                </div>
                <div className="modal-body">
                  <p>{conflictModal.message}</p>
                  <hr className="mb-24" />
                  {Array.isArray(conflictModal.conflicts) && conflictModal.conflicts.length > 0 ? (
                    <ul className="list-unstyled mb-0">
                      {conflictModal.conflicts.map((conflict) => {
                        const statusText = formatStatusLabel(conflict.status) || 'Event';
                        const eventName = conflict.name || `Event ${conflict.id}`;
                        const eventType = conflict.eventType ? `${conflict.eventType}` : '';
                        const startText = formatIntDateTime(conflict.start);
                        const endText = formatIntDateTime(conflict.end);
                        return (
                          <li key={conflict.id} className="mb-2">
                            Conflicting event <strong>{eventName}</strong> with status <strong>{statusText}</strong>.
                            This is a <strong>{eventType}</strong> event.<br />
                            <span className="my-16">
                              <span className="ms-24 me-8 fw-bold">{startText}</span>
                              <span className="d-inline-flex align-items-center mx-2" style={{ width: '240px' }}>
                                <span
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: 'var(--bs-primary)'
                                  }}
                                />
                                <span
                                  style={{
                                    flex: 1,
                                    height: '2px',
                                    background: 'var(--bs-primary)',
                                    margin: '0 4px'
                                  }}
                                />
                                <span
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: 'var(--bs-primary)'
                                  }}
                                />
                              </span>
                              <span className="ms-8 fw-bold">{endText}</span>
                            </span>
                          </li>

                        );
                      })}
                    </ul>
                  ) : (
                    <p className="mb-0">No conflict details were provided.</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setConflictModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showCommentsModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Event Comments
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowCommentsModal(false);
                      setCommentsEvent(null);
                      setCommentsHistory([]);
                    }}
                  />
                </div>
                <div className="modal-body">
                  {commentsLoading && (
                    <div className="text-center py-3">
                      <span className="spinner-border spinner-border-sm" />
                    </div>
                  )}
                  {!commentsLoading && (
                    <>
                      {Array.isArray(commentsHistory) && commentsHistory.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                          {commentsHistory.map((comment) => {
                            const userName = comment?.user?.name || 'Unknown User';
                            const imageKey = getUserImageKey(comment?.user);
                            const userImageUrl =
                              imageKey && /^https?:/i.test(imageKey)
                                ? imageKey
                                : imageKey
                                  ? `${process.env.REACT_APP_SPACES_BASE}/${imageKey}`
                                  : null;
                            const initials = getUserInitials(userName);
                            const timestamp = comment?.createdAt || comment?.created_at;
                            const formattedTimestamp = formatCommentTimestamp(timestamp);
                            const photos = Array.isArray(comment?.photos) ? comment.photos : [];

                            return (
                              <div key={comment.id || `${timestamp}-${userName}`}>
                                <div className="d-flex gap-3 align-items-start">
                                  <div
                                    className="flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
                                    style={{ width: '35px', height: '35px', overflow: 'hidden' }}
                                  >
                                    {userImageUrl ? (
                                      <img
                                        src={userImageUrl}
                                        alt={userName}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <span className="fw-semibold">{initials}</span>
                                    )}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex flex-wrap justify-content-start align-items-center gap-2">
                                      <strong>{userName}</strong>
                                      <span className="text-muted text-sm">{formattedTimestamp}</span>
                                    </div>
                                    <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                      {comment?.comments || comment?.comment || ''}
                                    </div>
                                    {photos.length > 0 && (
                                      <div className="d-flex flex-wrap gap-2 mt-2">
                                        {photos.map((photoKey, index) => {
                                          const photoUrl =
                                            photoKey && /^https?:/i.test(photoKey)
                                              ? photoKey
                                              : `${process.env.REACT_APP_SPACES_BASE}/${photoKey}`;
                                          return (
                                            <div
                                              key={`${comment.id || timestamp || index}-${photoKey}-${index}`}
                                              style={{
                                                width: '90px',
                                                height: '90px',
                                                borderRadius: '6px',
                                                overflow: 'hidden',
                                                border: '1px solid #ddd',
                                              }}
                                            >
                                              <img
                                                src={photoUrl}
                                                alt={`Comment attachment ${index + 1}`}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                              />
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="mb-0">No comments available for this event.</p>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowCommentsModal(false);
                      setCommentsEvent(null);
                      setCommentsHistory([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showDelaysListModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Event Delays
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowDelaysListModal(false);
                      setDelaysEvent(null);
                      setDelaysHistory([]);
                    }}
                  />
                </div>
                <div className="modal-body">
                  {delaysListLoading && (
                    <div className="text-center py-3">
                      <span className="spinner-border spinner-border-sm" />
                    </div>
                  )}
                  {!delaysListLoading && (
                    <>
                      {Array.isArray(delaysHistory) && delaysHistory.length > 0 ? (
                        <div className="table-responsive bordered-table">
                          <table className="table mb-0">
                            <thead>
                              <tr>
                                <th scope="col">Delay Code</th>
                                <th scope="col">Start Date</th>
                                <th scope="col">End Date</th>
                                <th scope="col">Duration (hh:mm)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {delaysHistory.map((delay) => (
                                <tr key={delay.id || `${delay.delayStartDate}-${delay.delayEndDate}`}>
                                  <td>{delay?.eventDelayCode?.name || delay?.eventDelayCode?.code || 'Delay'}</td>
                                  <td>{formatIntDateTime(delay?.delayStartDate)}</td>
                                  <td>{formatIntDateTime(delay?.delayEndDate)}</td>
                                  <td>{formatDurationHHMM(delay?.delayStartDate, delay?.delayEndDate)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="mb-0">No delays available for this event.</p>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowDelaysListModal(false);
                      setDelaysEvent(null);
                      setDelaysHistory([]);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {eventDialog && (
          <>
            <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{eventDialog.title || 'Notice'}</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => closeEventDialog(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <p className="mb-0">{eventDialog.message}</p>
                  </div>
                  <div className="modal-footer">
                    {eventDialog.showCancel && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => closeEventDialog(false)}
                      >
                        {eventDialog.cancelText || 'Cancel'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => closeEventDialog(true)}
                    >
                      {eventDialog.confirmText || 'OK'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop show" />
          </>
        )}
        {
          showDelayModal && (
            <div className="modal show d-block" tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editingDelay ? 'Edit Delay' : 'Add Delay'}</h5>
                    <button className="btn-close" onClick={() => setShowDelayModal(false)} />
                  </div>
                  <div className="modal-body">
                    <div className="row g-3">
                      {delayFields.map(renderDelayField)}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowDelayModal(false)}>Close</button>
                    <button className="btn btn-primary" onClick={handleDelaySave}>Save</button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {
          showAssignModal && (
            <div className="modal show d-block" tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Assign Work Orders</h5>
                    <button className="btn-close" onClick={() => setShowAssignModal(false)} />
                  </div>
                  <div className="modal-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <input
                        type="text"
                        className="form-control w-auto"
                        placeholder="Search..."
                        value={assignSearch}
                        onChange={(e) => setAssignSearch(e.target.value)}
                      />
                      <div className="d-flex gap-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSelectAllAssign}>
                          Select All
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleDeselectAllAssign}>
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: "calc(100vh - 500px)" }}>
                      <table className="table bordered-table mb-0 w-100 rounded-0">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Number</th>
                            <th>Title</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAssignOptions.map((wo) => (
                            <tr key={wo.id}>
                              <td>
                                <div className="form-check style-check d-flex justify-content-center">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={assignSelected.includes(wo.id)}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setAssignSelected((prev) =>
                                        checked
                                          ? [...prev, wo.id]
                                          : prev.filter((id) => id !== wo.id)
                                      );
                                    }}
                                  />
                                </div>
                              </td>
                              <td>{wo.workOrderNumber}</td>
                              <td>{wo.workOrderTitle}</td>
                            </tr>
                          ))}
                          {filteredAssignOptions.length === 0 && (
                            <tr>
                              <td colSpan="3" className="text-center py-2">
                                No Work Orders Available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleAssignSelected}>
                      Assign
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {
          showEditWoModal && (
            <div className="modal show d-block" tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Work Order</h5>
                    <button className="btn-close" onClick={() => setShowEditWoModal(false)} />
                  </div>
                  <div className="modal-body">
                    <div className="mb-3 fw-bold">
                      #{editingWo?.workOrderNumber} - {editingWo?.workOrderTitle}
                    </div>
                    <div className="row g-3">
                      {editWoFields.map((fieldDef) => {
                        if (
                          fieldDef.field === 'completedAt' &&
                          workOrderStatuses.find((s) => String(s.value) === String(editWoForm.statusId))?.label?.toLowerCase() !==
                          'complete'
                        ) {
                          return null;
                        }
                        return (
                          <React.Fragment key={fieldDef.field}>
                            {renderEditWoField(fieldDef)}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowEditWoModal(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSaveEditWo}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      </div >
    </>
  );
};

export default EventsLayer;
